import { useEffect, useState } from "react";
import { CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { FOLLOWUP_TYPES } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export default function CompletedHistory() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/followups").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading history…</div>;

  const done = items.filter((f) => f.status === "done");
  const filtered = done.filter((f) => {
    const s = q.toLowerCase();
    return !s || `${f.title} ${f.notes || ""} ${f.resolution_notes || ""}`.toLowerCase().includes(s);
  });
  const thisMonth = done.filter((f) => (f.completed_at || "").startsWith(new Date().toISOString().slice(0, 7)));

  return (
    <div className="space-y-6" data-testid="completed-history-page">
      <PageHeader overline="Follow-ups" title="Completed History" subtitle="An audit trail of what was chased and how it was resolved" />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Completed all time" value={done.length} tone="text-emerald-600" testid="stat-done-all" />
        <Stat label="Completed this month" value={thisMonth.length} testid="stat-done-month" />
        <Stat label="With resolution notes" value={done.filter((f) => f.resolution_notes).length} testid="stat-with-notes" />
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search completed follow-ups…" className="pl-9" data-testid="history-search" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={CheckCircle}>{done.length === 0 ? "Nothing completed yet — closed follow-ups will be archived here." : "No completed follow-ups match your search."}</EmptyState>
      ) : (
        <div className="space-y-2">
          {filtered.map((f) => (
            <Card key={f.id} className="p-4 border-border" data-testid={`history-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={17} weight="fill" className="text-emerald-500 shrink-0" />
                    <span className="font-medium text-primary truncate">{f.title}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 ml-6">
                    {FOLLOWUP_TYPES[f.followup_type] || "Other"} · due <span className="font-mono">{(f.due_date || "").slice(0, 10)}</span>
                    {f.completed_by_name ? ` · closed by ${f.completed_by_name}` : ""}
                  </div>
                  {f.resolution_notes && <div className="text-sm text-muted-foreground mt-2 ml-6">{f.resolution_notes}</div>}
                </div>
                {f.completed_at && <div className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">{new Date(f.completed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
